<template>
  <div class="pmc-page">
    <el-card shadow="hover" class="pmc-card">
      <div slot="header" class="pmc-header">
        <div>
          <h3>商品管理配置</h3>
          <p>统一维护商品分类、随心订套餐、超级碗系列与首页热销推荐；上架商品时从配置中选择</p>
        </div>
        <el-button class="btn-primary" :loading="saving" icon="el-icon-check" @click="save">保存配置</el-button>
      </div>

      <el-tabs v-model="activeTab">
        <el-tab-pane label="商品分类" name="categories">
          <p class="pmc-hint">
            分类会出现在「商品管理 → 添加/编辑商品」的下拉中，顺序即小程序订购页从左到右、从上到下的板块顺序。
            按住左侧「⋮⋮」拖动调整顺序，松手后按列表从上到下自动记为 1、2、3…，保存后小程序整块分类会跟着移动。
            关闭「小程序显示」后，订购套餐页不再出现该分类及其商品。
            打开「规格板块」后，该分类商品可配置板块与选项；关闭则按普通商品直接加购。
          </p>
          <div
            class="pmc-cat-list"
            @dragover.prevent="onListDragOver($event, 'cat')"
            @drop.prevent="onListDrop($event, 'cat')"
          >
            <div
              v-for="(cat, idx) in form.categories"
              :key="categoryKeys[idx] || ('cat-' + idx)"
              class="pmc-cat-row"
              :data-cat-index="idx"
            >
              <span
                class="pmc-drag-handle"
                title="拖动排序"
                draggable="true"
                @dragstart="onDragStart('cat', idx, $event)"
                @dragend="onDragEnd"
              >⋮⋮</span>
              <span class="pmc-cat-index">{{ idx + 1 }}</span>
              <div class="pmc-cat-sort">
                <button type="button" class="pmc-cat-move" :disabled="idx === 0" @click="moveCategory(idx, -1)">上移</button>
                <button
                  type="button"
                  class="pmc-cat-move"
                  :disabled="idx >= form.categories.length - 1"
                  @click="moveCategory(idx, 1)"
                >下移</button>
              </div>
              <el-input
                v-model="form.categories[idx]"
                class="pmc-cat-name"
                maxlength="20"
                placeholder="分类名称"
              />
              <div
                class="pmc-spec-panel"
                :class="{
                  'is-on': displayEnabled(cat),
                  'is-disabled': !String(cat || '').trim()
                }"
              >
                <div class="pmc-spec-copy">
                  <span class="pmc-spec-title">小程序显示</span>
                  <span class="pmc-spec-state">{{ displayStateText(cat) }}</span>
                </div>
                <el-switch
                  :value="displayEnabled(cat)"
                  :disabled="!String(cat || '').trim()"
                  active-color="#2bb673"
                  inactive-color="#cbd5e1"
                  @change="(v) => toggleDisplayCategory(cat, v)"
                />
              </div>
              <div
                class="pmc-spec-panel"
                :class="{
                  'is-on': specEnabled(cat),
                  'is-disabled': !String(cat || '').trim()
                }"
              >
                <div class="pmc-spec-copy">
                  <span class="pmc-spec-title">规格板块</span>
                  <span class="pmc-spec-state">{{ specStateText(cat) }}</span>
                </div>
                <el-switch
                  :value="specEnabled(cat)"
                  :disabled="!String(cat || '').trim()"
                  active-color="#2bb673"
                  inactive-color="#cbd5e1"
                  @change="(v) => toggleSpecCategory(cat, v)"
                />
              </div>
              <button
                type="button"
                class="pmc-cat-remove"
                title="删除分类"
                :disabled="form.categories.length <= 1"
                @click="removeCategory(idx)"
              >
                <i class="el-icon-delete" />
              </button>
            </div>
          </div>
          <el-button size="small" icon="el-icon-plus" @click="addCategory">添加分类</el-button>
        </el-tab-pane>

        <el-tab-pane label="随心订套餐" name="subscription">
          <p class="pmc-hint">控制商品表单中展示的套餐类型，以及周/月/季/年的默认天数基数。</p>
          <el-table :data="form.subscription_package_types" border size="small" class="pmc-table">
            <el-table-column label="类型标识" width="120" prop="value" />
            <el-table-column label="显示名称" min-width="140">
              <template slot-scope="scope">
                <el-input v-model="scope.row.label" maxlength="20" />
              </template>
            </el-table-column>
            <el-table-column label="启用" width="100" align="center">
              <template slot-scope="scope">
                <el-switch v-model="scope.row.enabled" />
              </template>
            </el-table-column>
            <el-table-column label="默认天数基数" width="180">
              <template slot-scope="scope">
                <el-input-number
                  v-if="scope.row.value !== 'custom'"
                  v-model="presetForm[scope.row.value]"
                  :min="1"
                  :max="scope.row.value === 'year' ? 9999 : 999"
                  controls-position="right"
                  style="width: 100%"
                />
                <span v-else class="pmc-muted">自定义由商家填写</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="超级碗系列" name="specs">
          <p class="pmc-hint">
            在此维护板块名称、选项名称和选项照片。上架商品时选择板块名称后，将自动带入该板块下已上架的全部选项（含照片）。
            板块与选项均可按住「⋮⋮」拖动排序，松手后按实际顺序自动重排为 0、1、2…（越小越靠前）。
            商品上架后，保存配置只同步各商品已有选项的「图片」和「库存状态」；不会改商品的最少/最多选择、不会增删选项、不会改加价。需要改选择份数或选项列表，请到「商品管理」编辑该商品。
            「库存」设为售罄后，所有含该选项的商品在小程序/收银均显示售罄且不可选；关闭「上架」后，该选项不再出现在小程序与收银。
          </p>
          <div class="pmc-tpl-toolbar">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="addTemplate">添加板块模板</el-button>
          </div>

          <div
            class="pmc-tpl-list"
            @dragover.prevent="onListDragOver($event, 'tpl')"
            @drop.prevent="onListDrop($event, 'tpl')"
          >
          <div
            v-for="(tpl, tIndex) in form.spec_templates"
            :key="tpl.id || ('tpl-' + tIndex)"
            class="pmc-tpl-card"
            :data-tpl-index="tIndex"
          >
            <div class="pmc-tpl-head">
              <div class="pmc-tpl-title">
                <span
                  class="pmc-drag-handle"
                  title="拖动板块排序"
                  draggable="true"
                  @dragstart="onDragStart('tpl', tIndex, $event)"
                  @dragend="onDragEnd"
                >⋮⋮</span>
                <span class="pmc-badge">{{ tIndex + 1 }}</span>
                <el-input
                  v-model="tpl.group_name"
                  maxlength="50"
                  placeholder="板块名称，如：选择碳水或沙拉"
                  class="pmc-tpl-name"
                />
                <el-switch
                  v-model="tpl.enabled"
                  active-text="上架"
                  inactive-text="下架"
                />
              </div>
              <div class="pmc-tpl-actions">
                <el-button type="text" :disabled="tIndex === 0" @click="moveTemplate(tIndex, -1)">上移</el-button>
                <el-button
                  type="text"
                  :disabled="tIndex >= form.spec_templates.length - 1"
                  @click="moveTemplate(tIndex, 1)"
                >下移</el-button>
                <el-button type="text" class="danger" @click="removeTemplate(tIndex)">删除</el-button>
              </div>
            </div>

            <div class="pmc-tpl-meta">
              <el-form-item label="最少选择" label-width="80px">
                <el-input-number v-model="tpl.min_select" :min="0" :max="99" controls-position="right" />
              </el-form-item>
              <el-form-item label="最多选择" label-width="80px">
                <el-input-number v-model="tpl.max_select" :min="0" :max="99" controls-position="right" />
                <span class="pmc-muted">0 = 不限制</span>
              </el-form-item>
            </div>

            <div class="pmc-opt-head">
              <strong>板块选项</strong>
              <span class="pmc-muted">拖「⋮⋮」调整顺序，排序值自动按列表从 0 起递增</span>
              <el-button size="mini" icon="el-icon-plus" @click="addOption(tIndex)">添加选项</el-button>
            </div>

            <div
              class="pmc-opt-table"
              v-if="tpl.options && tpl.options.length"
              :data-t-index="tIndex"
              @dragover.prevent="onListDragOver($event, 'opt', tIndex)"
              @drop.prevent="onListDrop($event, 'opt', tIndex)"
            >
              <div class="pmc-opt-thead">
                <span class="pmc-opt-col-drag" />
                <span class="pmc-opt-col-sort">排序</span>
                <span class="pmc-opt-col-name">选项名称</span>
                <span class="pmc-opt-col-photo">照片</span>
                <span class="pmc-opt-col-price">加价</span>
                <span class="pmc-opt-col-stock">库存</span>
                <span class="pmc-opt-col-switch">上架</span>
                <span class="pmc-opt-col-switch">推荐</span>
                <span class="pmc-opt-col-act">操作</span>
              </div>
              <div
                v-for="(opt, oIndex) in tpl.options"
                :key="opt._uid || ((tpl.id || tIndex) + '-opt-' + oIndex)"
                class="pmc-opt-row"
                :data-opt-index="oIndex"
              >
                <span
                  class="pmc-drag-handle"
                  title="拖动排序"
                  draggable="true"
                  @dragstart.stop="onDragStart('opt', oIndex, $event, tIndex)"
                  @dragend="onDragEnd"
                >⋮⋮</span>
                <span class="pmc-opt-col-sort pmc-opt-sort-num">{{ oIndex }}</span>
                <div class="pmc-opt-col-name">
                  <el-input v-model="opt.option_name" maxlength="50" placeholder="如：6种谷物饭" size="mini" />
                </div>
                <div class="pmc-opt-col-photo">
                  <div class="pmc-opt-photo">
                    <el-upload
                      class="pmc-opt-upload"
                      :show-file-list="false"
                      accept="image/jpeg,image/png,image/webp"
                      :http-request="(param) => uploadOptionImage(param, tIndex, oIndex)"
                    >
                      <img
                        v-if="opt.option_image"
                        :src="resolveImg(opt.option_image)"
                        class="pmc-opt-thumb"
                        alt=""
                      />
                      <span v-else class="pmc-opt-add"><i class="el-icon-plus" /></span>
                    </el-upload>
                    <button
                      v-if="opt.option_image"
                      type="button"
                      class="pmc-opt-photo-clear"
                      title="移除照片"
                      @click.stop="clearOptionImage(tIndex, oIndex)"
                    >
                      <i class="el-icon-close" />
                    </button>
                  </div>
                </div>
                <div class="pmc-opt-col-price">
                  <el-input-number
                    v-model="opt.extra_price"
                    :min="0"
                    :precision="2"
                    :step="1"
                    size="mini"
                    controls-position="right"
                    style="width: 100%"
                  />
                </div>
                <div class="pmc-opt-col-stock">
                  <el-select v-model="opt.stock_status" size="mini" style="width: 100%">
                    <el-option :value="1" label="正常" />
                    <el-option :value="0" label="售罄" />
                  </el-select>
                </div>
                <div class="pmc-opt-col-switch">
                  <el-switch v-model="opt.enabled" />
                </div>
                <div class="pmc-opt-col-switch">
                  <el-switch v-model="opt.is_recommend" />
                </div>
                <div class="pmc-opt-col-act">
                  <el-button type="text" class="danger" @click="removeOption(tIndex, oIndex)">删除</el-button>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无选项" :image-size="64" />
          </div>

          <el-empty v-if="!(form.spec_templates && form.spec_templates.length)" description="暂无超级碗系列，点击上方添加" />
          </div>
        </el-tab-pane>

        <el-tab-pane label="热销推荐设置" name="hot">
          <p class="pmc-hint">
            选择首页「热销推荐」展示的商品，来源为商品管理中的全部商品（含新上架）。
            支持下拉选择与输入名称筛选；展示顺序为下方列表顺序，可按住「⋮⋮」拖动或用上移/下移调整。
            未选择任何商品时，小程序仍按原逻辑展示前 3 个随心订商品。
          </p>
          <div class="pmc-hot-picker">
            <el-select
              v-model="hotPickerValue"
              multiple
              filterable
              clearable
              collapse-tags
              placeholder="输入商品名称筛选，或点击下拉选择"
              style="width: 100%;"
              :loading="productsLoading"
              @visible-change="onHotSelectVisible"
              @change="onHotPickerChange"
            >
              <el-option
                v-for="p in productOptions"
                :key="p.id"
                :label="p.label"
                :value="p.id"
              >
                <span class="pmc-hot-opt-name">{{ p.name }}</span>
                <span class="pmc-hot-opt-meta">{{ p.category }} · ¥{{ p.priceText }}</span>
              </el-option>
            </el-select>
            <el-button
              size="small"
              icon="el-icon-refresh"
              :loading="productsLoading"
              class="pmc-hot-refresh"
              @click="loadProductOptions"
            >刷新商品列表</el-button>
          </div>

          <div v-if="selectedHotProducts.length" class="pmc-hot-selected">
            <div class="pmc-hot-selected-head">
              <strong>已选 {{ selectedHotProducts.length }} 件</strong>
              <span class="pmc-muted">顺序即首页展示顺序</span>
            </div>
            <div
              class="pmc-hot-list"
              @dragover.prevent="onListDragOver($event, 'hot')"
              @drop.prevent="onListDrop($event, 'hot')"
            >
            <div
              v-for="(p, idx) in selectedHotProducts"
              :key="p.id"
              class="pmc-hot-row"
              :data-hot-index="idx"
            >
              <span
                class="pmc-drag-handle"
                title="拖动排序"
                draggable="true"
                @dragstart="onDragStart('hot', idx, $event)"
                @dragend="onDragEnd"
              >⋮⋮</span>
              <span class="pmc-cat-index">{{ idx + 1 }}</span>
              <div class="pmc-cat-sort">
                <button type="button" class="pmc-cat-move" :disabled="idx === 0" @click="moveHotProduct(idx, -1)">上移</button>
                <button
                  type="button"
                  class="pmc-cat-move"
                  :disabled="idx >= selectedHotProducts.length - 1"
                  @click="moveHotProduct(idx, 1)"
                >下移</button>
              </div>
              <div class="pmc-hot-info">
                <span class="pmc-hot-name">{{ p.name }}</span>
                <span class="pmc-muted">{{ p.category }} · ¥{{ p.priceText }}</span>
              </div>
              <button type="button" class="pmc-cat-remove" title="移除" @click="removeHotProduct(idx)">
                <i class="el-icon-delete" />
              </button>
            </div>
            </div>
          </div>
          <el-empty
            v-else
            description="尚未选择热销商品；保存后小程序将继续用默认随心订前 3 个"
          />
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import { routeTabMixin } from '../utils/routeTab'

function makeUid(prefix) {
  return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
}

function emptyOption(sort) {
  return {
    _uid: makeUid('opt'),
    option_name: '',
    extra_price: 0,
    stock_status: 1,
    enabled: true,
    sort: sort || 0,
    is_recommend: false,
    option_image: ''
  }
}

function emptyTemplate(sort) {
  return {
    id: 'tpl-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    group_name: '',
    enabled: true,
    min_select: 1,
    max_select: 1,
    sort: sort || 0,
    options: [emptyOption(0)]
  }
}

export default {
  name: 'ProductMgmtConfig',
  mixins: [
    routeTabMixin({
      allowed: ['categories', 'subscription', 'specs', 'hot']
    })
  ],
  data() {
    return {
      loading: false,
      saving: false,
      productsLoading: false,
      activeTab: 'categories',
      productOptions: [],
      hotPickerValue: [],
      categoryKeys: [],
      form: {
        categories: [],
        hidden_categories: [],
        spec_config_categories: [],
        subscription_package_types: [],
        subscription_presets: {},
        spec_templates: [],
        hot_recommend_product_ids: []
      },
      presetForm: { week: 5, month: 22, quarter: 66, year: 264 }
    }
  },
  computed: {
    assetOrigin() {
      const base =
        (this.$api && this.$api.defaults && this.$api.defaults.baseURL) ||
        (typeof window !== 'undefined' ? window.location.origin + '/api' : '')
      return String(base || '').replace(/\/api\/?$/, '') || (typeof window !== 'undefined' ? window.location.origin : '')
    },
    productById() {
      const map = {}
      ;(this.productOptions || []).forEach((p) => {
        if (p && p.id) map[String(p.id)] = p
      })
      return map
    },
    selectedHotProducts() {
      return (this.form.hot_recommend_product_ids || [])
        .map((id) => {
          const p = this.productById[String(id)]
          if (p) return p
          return {
            id: String(id),
            name: '（商品已删除或未加载）',
            category: '-',
            priceText: '-',
            missing: true
          }
        })
        .filter(Boolean)
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'hot') this.loadProductOptions()
    }
  },
  mounted() {
    this.load()
    this.loadProductOptions()
  },
  beforeDestroy() {
    if (this._dragOverRaf) cancelAnimationFrame(this._dragOverRaf)
    this._clearDragOverEl()
  },
  methods: {
    resolveImg(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s) || /^data:/i.test(s)) return s
      if (s.startsWith('/')) return this.assetOrigin + s
      return s
    },
    formatPriceText(price) {
      const n = Number(price)
      if (!Number.isFinite(n)) return '0'
      return Number.isInteger(n) ? String(n) : n.toFixed(2)
    },
    async loadProductOptions() {
      this.productsLoading = true
      try {
        const resp = await this.$api.get('/products', {
          params: { page: 1, pageSize: 500, include_off: 1 }
        })
        const list =
          resp && resp.code === 200 && Array.isArray(resp.data)
            ? resp.data
            : []
        this.productOptions = list.map((p) => {
          const id = String(p.id)
          const name = String(p.name || '').trim() || id
          const category = String(p.category || '').trim() || '未分类'
          const priceText = this.formatPriceText(p.price)
          const status =
            p.sale_status === 'off'
              ? '已下架'
              : p.sale_status === 'sold_out' || p.is_sold_out
                ? '售罄'
                : ''
          return {
            id,
            name,
            category,
            priceText,
            label: status
              ? `${name}（${category} · ¥${priceText} · ${status}）`
              : `${name}（${category} · ¥${priceText}）`
          }
        })
        // 同步选择器展示值
        this.hotPickerValue = (this.form.hot_recommend_product_ids || []).slice()
      } catch (_e) {
        this.$message.warning('商品列表加载失败，可稍后点「刷新商品列表」')
      } finally {
        this.productsLoading = false
      }
    },
    onHotSelectVisible(visible) {
      if (visible && !(this.productOptions && this.productOptions.length)) {
        this.loadProductOptions()
      }
    },
    onHotPickerChange(ids) {
      const next = Array.isArray(ids) ? ids.map((id) => String(id)) : []
      // 保留原顺序中仍选中的，再追加新选的
      const prev = this.form.hot_recommend_product_ids || []
      const keep = prev.filter((id) => next.includes(String(id)))
      const added = next.filter((id) => !keep.includes(String(id)))
      this.form.hot_recommend_product_ids = keep.concat(added)
      this.hotPickerValue = this.form.hot_recommend_product_ids.slice()
    },
    moveHotProduct(idx, delta) {
      const list = (this.form.hot_recommend_product_ids || []).slice()
      const next = idx + delta
      if (next < 0 || next >= list.length) return
      const row = list.splice(idx, 1)[0]
      list.splice(next, 0, row)
      this.form.hot_recommend_product_ids = list
      this.hotPickerValue = list.slice()
    },
    removeHotProduct(idx) {
      const list = (this.form.hot_recommend_product_ids || []).slice()
      list.splice(idx, 1)
      this.form.hot_recommend_product_ids = list
      this.hotPickerValue = list.slice()
    },
    async load() {
      this.loading = true
      try {
        const resp = await this.$api.get('/products/mgmt-config')
        if (resp && resp.code === 200 && resp.data) {
          this.applyConfig(resp.data)
        }
      } catch (e) {
        this.$message.error('加载配置失败')
      } finally {
        this.loading = false
      }
    },
    applyConfig(data) {
      const templates = Array.isArray(data.spec_templates)
        ? data.spec_templates
            .slice()
            .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
            .map((t) => {
              const options = (Array.isArray(t.options) ? t.options.slice() : [])
                .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
                .map((o, oi) => ({
                  ...o,
                  _uid: o._uid || makeUid('opt'),
                  sort: oi
                }))
              return {
                ...t,
                id: t.id || makeUid('tpl'),
                options,
                sort: 0
              }
            })
        : []
      templates.forEach((t, ti) => {
        t.sort = ti
      })
      const categories = Array.isArray(data.categories) ? data.categories.slice() : []
      this.categoryKeys = categories.map(() => makeUid('cat'))
      this.form = {
        categories,
        hidden_categories: Array.isArray(data.hidden_categories)
          ? data.hidden_categories.slice()
          : [],
        spec_config_categories: Array.isArray(data.spec_config_categories)
          ? data.spec_config_categories.slice()
          : [],
        subscription_package_types: Array.isArray(data.subscription_package_types)
          ? data.subscription_package_types.map((p) => ({ ...p }))
          : [],
        subscription_presets: data.subscription_presets || {},
        spec_templates: templates,
        hot_recommend_product_ids: Array.isArray(data.hot_recommend_product_ids)
          ? data.hot_recommend_product_ids.map((id) => String(id))
          : []
      }
      this.hotPickerValue = this.form.hot_recommend_product_ids.slice()
      const presets = data.subscription_presets || {}
      this.presetForm = {
        week: (presets.week && presets.week.base_meals) || 5,
        month: (presets.month && presets.month.base_meals) || 22,
        quarter: (presets.quarter && presets.quarter.base_meals) || 66,
        year: (presets.year && presets.year.base_meals) || 264
      }
    },
    onDragStart(kind, index, e, tIndex) {
      this._drag = {
        kind,
        index,
        tIndex: tIndex == null ? -1 : tIndex,
        overIndex: -1
      }
      this._clearDragOverEl()
      if (this.$el) this.$el.classList.add('is-dragging')
      if (e && e.dataTransfer) {
        try {
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', String(index))
          if (e.dataTransfer.setDragImage && e.currentTarget) {
            e.dataTransfer.setDragImage(e.currentTarget, 12, 12)
          }
        } catch (_err) { /* ignore */ }
      }
    },
    dragIndexAttr(kind) {
      if (kind === 'cat') return 'data-cat-index'
      if (kind === 'tpl') return 'data-tpl-index'
      if (kind === 'opt') return 'data-opt-index'
      if (kind === 'hot') return 'data-hot-index'
      return ''
    },
    findDragRow(e, kind) {
      const attr = this.dragIndexAttr(kind)
      if (!attr || !e || !e.target || !e.target.closest) return null
      const row = e.target.closest('[' + attr + ']')
      if (!row || (e.currentTarget && !e.currentTarget.contains(row))) return null
      return row
    },
    onListDragOver(e, kind, tIndex) {
      const drag = this._drag
      if (!drag || drag.kind !== kind) return
      if (kind === 'opt' && drag.tIndex !== tIndex) return
      const row = this.findDragRow(e, kind)
      if (!row) return
      const index = Number(row.getAttribute(this.dragIndexAttr(kind)))
      if (!Number.isFinite(index) || index === drag.overIndex) return
      drag.overIndex = index
      if (this._dragOverRaf) cancelAnimationFrame(this._dragOverRaf)
      this._dragOverRaf = requestAnimationFrame(() => {
        this._dragOverRaf = 0
        this._setDragOverEl(row)
      })
    },
    onListDrop(e, kind, tIndex) {
      const drag = this._drag
      if (!drag || drag.kind !== kind) {
        this.onDragEnd()
        return
      }
      if (kind === 'opt' && drag.tIndex !== tIndex) {
        this.onDragEnd()
        return
      }
      const row = this.findDragRow(e, kind)
      const attr = this.dragIndexAttr(kind)
      const toIndex = row
        ? Number(row.getAttribute(attr))
        : drag.overIndex
      if (!Number.isFinite(toIndex) || toIndex < 0) {
        this.onDragEnd()
        return
      }
      const from = drag.index
      if (kind === 'cat') this.applyCatReorder(from, toIndex)
      else if (kind === 'tpl') this.applyTplReorder(from, toIndex)
      else if (kind === 'opt') this.applyOptReorder(tIndex, from, toIndex)
      else if (kind === 'hot') this.applyHotReorder(from, toIndex)
      this.onDragEnd()
    },
    onDragEnd() {
      this._drag = null
      if (this._dragOverRaf) {
        cancelAnimationFrame(this._dragOverRaf)
        this._dragOverRaf = 0
      }
      this._clearDragOverEl()
      if (this.$el) this.$el.classList.remove('is-dragging')
    },
    _setDragOverEl(el) {
      if (this._dragOverEl === el) return
      if (this._dragOverEl) this._dragOverEl.classList.remove('is-drag-over')
      this._dragOverEl = el || null
      if (this._dragOverEl) this._dragOverEl.classList.add('is-drag-over')
    },
    _clearDragOverEl() {
      if (this._dragOverEl) {
        this._dragOverEl.classList.remove('is-drag-over')
        this._dragOverEl = null
      }
    },
    moveArrayItem(arr, from, to) {
      if (!Array.isArray(arr) || from === to || from < 0 || to < 0) return false
      if (from >= arr.length || to >= arr.length) return false
      const item = arr.splice(from, 1)[0]
      arr.splice(to, 0, item)
      return true
    },
    reindexTemplateSorts() {
      const list = this.form.spec_templates || []
      for (let i = 0; i < list.length; i++) list[i].sort = i
    },
    reindexOptionSorts(tIndex) {
      const tpl = this.form.spec_templates[tIndex]
      if (!tpl || !Array.isArray(tpl.options)) return
      for (let i = 0; i < tpl.options.length; i++) tpl.options[i].sort = i
    },
    applyCatReorder(from, to) {
      const list = this.form.categories.slice()
      const keys = this.categoryKeys.slice()
      if (!this.moveArrayItem(list, from, to)) return
      this.moveArrayItem(keys, from, to)
      this.form.categories = list
      this.categoryKeys = keys
    },
    applyTplReorder(from, to) {
      if (this.moveArrayItem(this.form.spec_templates, from, to)) {
        this.reindexTemplateSorts()
      }
    },
    applyOptReorder(tIndex, from, to) {
      const tpl = this.form.spec_templates[tIndex]
      if (!tpl || !Array.isArray(tpl.options)) return
      if (this.moveArrayItem(tpl.options, from, to)) {
        this.reindexOptionSorts(tIndex)
      }
    },
    applyHotReorder(from, to) {
      const list = (this.form.hot_recommend_product_ids || []).slice()
      if (!this.moveArrayItem(list, from, to)) return
      this.form.hot_recommend_product_ids = list
      this.hotPickerValue = list.slice()
    },
    addCategory() {
      this.form.categories.push('')
      this.categoryKeys.push(makeUid('cat'))
    },
    moveCategory(idx, delta) {
      const next = idx + delta
      if (next < 0 || next >= this.form.categories.length) return
      this.applyCatReorder(idx, next)
    },
    removeCategory(idx) {
      const name = this.form.categories[idx]
      this.form.categories.splice(idx, 1)
      this.categoryKeys.splice(idx, 1)
      this.form.spec_config_categories = this.form.spec_config_categories.filter((c) => c !== name)
      this.form.hidden_categories = this.form.hidden_categories.filter((c) => c !== name)
    },
    displayEnabled(cat) {
      const name = String(cat || '').trim()
      if (!name) return true
      return !this.form.hidden_categories.includes(name)
    },
    displayStateText(cat) {
      if (!String(cat || '').trim()) return '先填写分类名称'
      return this.displayEnabled(cat) ? '订购页可见' : '订购页已隐藏'
    },
    toggleDisplayCategory(cat, visible) {
      const name = String(cat || '').trim()
      if (!name) return
      const set = new Set(this.form.hidden_categories)
      if (visible) set.delete(name)
      else set.add(name)
      this.form.hidden_categories = Array.from(set)
    },
    specEnabled(cat) {
      const name = String(cat || '').trim()
      if (!name) return false
      return this.form.spec_config_categories.includes(name)
    },
    specStateText(cat) {
      if (!String(cat || '').trim()) return '先填写分类名称'
      return this.specEnabled(cat) ? '已开启 · 下单可选配' : '已关闭 · 普通加购'
    },
    toggleSpecCategory(cat, enabled) {
      const name = String(cat || '').trim()
      if (!name) return
      const set = new Set(this.form.spec_config_categories)
      if (enabled) set.add(name)
      else set.delete(name)
      this.form.spec_config_categories = Array.from(set)
    },
    addTemplate() {
      this.form.spec_templates.push(emptyTemplate(this.form.spec_templates.length))
      this.reindexTemplateSorts()
    },
    removeTemplate(idx) {
      this.form.spec_templates.splice(idx, 1)
      this.reindexTemplateSorts()
    },
    moveTemplate(idx, delta) {
      const next = idx + delta
      if (next < 0 || next >= this.form.spec_templates.length) return
      const arr = this.form.spec_templates
      const tmp = arr[idx]
      this.$set(arr, idx, arr[next])
      this.$set(arr, next, tmp)
      this.reindexTemplateSorts()
    },
    addOption(tIndex) {
      const tpl = this.form.spec_templates[tIndex]
      if (!tpl) return
      if (!Array.isArray(tpl.options)) this.$set(tpl, 'options', [])
      tpl.options.push(emptyOption(tpl.options.length))
      this.reindexOptionSorts(tIndex)
    },
    removeOption(tIndex, oIndex) {
      const tpl = this.form.spec_templates[tIndex]
      if (!tpl || !Array.isArray(tpl.options)) return
      tpl.options.splice(oIndex, 1)
      this.reindexOptionSorts(tIndex)
    },
    clearOptionImage(tIndex, oIndex) {
      const tpl = this.form.spec_templates[tIndex]
      const opt = tpl && tpl.options && tpl.options[oIndex]
      if (!opt) return
      this.$set(opt, 'option_image', '')
    },
    async uploadOptionImage(param, tIndex, oIndex) {
      if (!param || !param.file) return
      const formData = new FormData()
      formData.append('file', param.file)
      try {
        const resp = await this.$api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const url = resp && resp.data && resp.data.url
        const tpl = this.form.spec_templates[tIndex]
        const opt = tpl && tpl.options && tpl.options[oIndex]
        if (resp && resp.code === 200 && url && opt) {
          this.$set(opt, 'option_image', url)
          this.$message.success('照片已上传，请保存配置')
        } else {
          this.$message.error((resp && resp.message) || '照片上传失败')
        }
      } catch (e) {
        this.$message.error('照片上传失败')
      } finally {
        if (param.onSuccess) param.onSuccess()
      }
    },
    buildPayload() {
      const categories = (this.form.categories || [])
        .map((c) => String(c || '').trim())
        .filter(Boolean)
      const catSet = new Set(categories)
      return {
        categories,
        hidden_categories: (this.form.hidden_categories || [])
          .map((c) => String(c || '').trim())
          .filter((c) => catSet.has(c)),
        spec_config_categories: (this.form.spec_config_categories || []).filter((c) => catSet.has(c) || true),
        subscription_package_types: (this.form.subscription_package_types || []).map((p) => ({
          value: p.value,
          label: String(p.label || '').trim() || p.value,
          enabled: p.enabled !== false
        })),
        subscription_presets: {
          week: { base_meals: Number(this.presetForm.week) },
          month: { base_meals: Number(this.presetForm.month) },
          quarter: { base_meals: Number(this.presetForm.quarter) },
          year: { base_meals: Number(this.presetForm.year) }
        },
        spec_templates: (this.form.spec_templates || []).map((t, ti) => ({
          id: t.id,
          group_name: String(t.group_name || '').trim(),
          enabled: t.enabled !== false,
          min_select: t.min_select,
          max_select: t.max_select,
          sort: ti,
          options: (t.options || []).map((o, oi) => ({
            option_name: String(o.option_name || '').trim(),
            extra_price: Number(o.extra_price) || 0,
            stock_status: Number(o.stock_status) === 0 ? 0 : 1,
            enabled: o.enabled !== false,
            sort: oi,
            is_recommend: !!o.is_recommend,
            option_image: String(o.option_image || '').trim()
          }))
        })),
        hot_recommend_product_ids: (this.form.hot_recommend_product_ids || [])
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      }
    },
    async save() {
      const payload = this.buildPayload()
      if (!payload.categories.length) {
        this.$message.warning('请至少保留一个商品分类')
        this.activeTab = 'categories'
        return
      }
      for (let i = 0; i < payload.spec_templates.length; i++) {
        const t = payload.spec_templates[i]
        if (!t.group_name) {
          this.$message.warning(`第 ${i + 1} 个板块请填写板块名称`)
          this.activeTab = 'specs'
          return
        }
        if (!t.options.filter((o) => o.option_name).length) {
          this.$message.warning(`板块「${t.group_name}」请至少添加一个选项`)
          this.activeTab = 'specs'
          return
        }
      }
      this.saving = true
      try {
        const resp = await this.$api.put('/products/mgmt-config', payload)
        if (resp && resp.code === 200) {
          const data = resp.data && typeof resp.data === 'object' ? { ...resp.data } : {}
          const echoed = Array.isArray(data.hidden_categories)
          if (!echoed) {
            data.hidden_categories = payload.hidden_categories
            this.applyConfig(data)
            this.$message.warning('服务器还是旧版本，小程序显示开关没有真正保存。请上传后端配置服务并重启后再保存一次。')
            return
          }
          this.applyConfig(data)
          this.$message.success((resp && resp.message) || '配置已保存')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.pmc-page {
  padding: 0;
}
.pmc-card {
  border-radius: 12px;
}
.pmc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.pmc-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}
.pmc-header p {
  margin: 0;
  font-size: 13px;
  color: #888;
  line-height: 1.5;
}
.pmc-hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}
.pmc-cat-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
  max-width: 1080px;
}
.pmc-cat-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: border-color 0.15s, background 0.15s;
}
.pmc-cat-row.is-drag-over,
.pmc-tpl-card.is-drag-over,
.pmc-opt-row.is-drag-over,
.pmc-hot-row.is-drag-over {
  border-color: #2bb673;
  background: #f0faf5;
}
.pmc-page.is-dragging {
  cursor: grabbing;
  user-select: none;
}
.pmc-page.is-dragging .pmc-cat-row,
.pmc-page.is-dragging .pmc-tpl-card,
.pmc-page.is-dragging .pmc-opt-row,
.pmc-page.is-dragging .pmc-hot-row {
  transition: none;
}
.pmc-page.is-dragging .pmc-opt-row > :not(.pmc-drag-handle),
.pmc-page.is-dragging .pmc-cat-row > :not(.pmc-drag-handle),
.pmc-page.is-dragging .pmc-hot-row > :not(.pmc-drag-handle) {
  pointer-events: none;
}
.pmc-drag-handle {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1;
  letter-spacing: -2px;
  cursor: grab;
  user-select: none;
  touch-action: none;
}
.pmc-drag-handle:active {
  cursor: grabbing;
  color: #2d6a4f;
  border-color: #b7dfc6;
}
.pmc-hot-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pmc-cat-index {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pmc-cat-sort {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}
.pmc-cat-move {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  cursor: pointer;
}
.pmc-cat-move:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}
.pmc-cat-name {
  flex: 1;
  min-width: 160px;
}
.pmc-spec-panel {
  width: 200px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}
.pmc-spec-panel.is-on {
  border-color: rgba(43, 182, 115, 0.45);
  background: #f3fbf7;
}
.pmc-spec-panel.is-disabled {
  opacity: 0.55;
}
.pmc-spec-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pmc-spec-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.2;
}
.pmc-spec-state {
  font-size: 12px;
  color: #64748b;
  line-height: 1.3;
}
.pmc-spec-panel.is-on .pmc-spec-state {
  color: #15803d;
}
.pmc-cat-remove {
  width: 36px;
  height: 36px;
  border: 1px solid #fecdd3;
  border-radius: 10px;
  background: #fff;
  color: #e11d48;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pmc-cat-remove:hover:not(:disabled) {
  background: #fff1f2;
}
.pmc-cat-remove:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.pmc-table {
  max-width: 720px;
}
.pmc-muted {
  color: #999;
  font-size: 12px;
  margin-left: 8px;
}
.pmc-tpl-toolbar {
  margin-bottom: 12px;
}
.pmc-tpl-card {
  border: 1px solid #e8ebe6;
  border-radius: 10px;
  padding: 14px 16px 18px;
  margin-bottom: 14px;
  background: #fafbf9;
  transition: border-color 0.15s, background 0.15s;
}
.pmc-tpl-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.pmc-tpl-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 280px;
}
.pmc-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #2d6a4f;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.pmc-tpl-name {
  flex: 1;
  max-width: 360px;
}
.pmc-tpl-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.pmc-tpl-meta .el-form-item {
  margin-bottom: 0;
}
.pmc-opt-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 10px;
  flex-wrap: wrap;
}
.pmc-opt-head .el-button {
  margin-left: auto;
}
.pmc-opt-table {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.pmc-opt-thead,
.pmc-opt-row {
  display: grid;
  grid-template-columns: 36px 48px minmax(140px, 1.4fr) 64px 120px 110px 64px 64px 64px;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
}
.pmc-opt-thead {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
}
.pmc-opt-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s, border-color 0.15s;
}
.pmc-opt-row:last-child {
  border-bottom: none;
}
.pmc-opt-col-drag,
.pmc-opt-col-sort,
.pmc-opt-col-photo,
.pmc-opt-col-switch,
.pmc-opt-col-act {
  display: flex;
  align-items: center;
  justify-content: center;
}
.pmc-opt-sort-num {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}
.pmc-opt-col-name,
.pmc-opt-col-price,
.pmc-opt-col-stock {
  min-width: 0;
}
@media (max-width: 1100px) {
  .pmc-opt-thead,
  .pmc-opt-row {
    grid-template-columns: 36px 40px minmax(120px, 1fr) 56px 100px 96px 56px 56px 56px;
  }
}
.danger {
  color: #c45656 !important;
}
.btn-primary {
  background: #2d6a4f;
  border-color: #2d6a4f;
  color: #fff;
}
.btn-primary:hover,
.btn-primary:focus {
  background: #1b4332;
  border-color: #1b4332;
  color: #fff;
}
.pmc-opt-photo {
  position: relative;
  display: inline-flex;
  width: 44px;
  height: 44px;
}
.pmc-opt-upload {
  width: 44px;
  height: 44px;
}
.pmc-opt-upload >>> .el-upload {
  width: 44px;
  height: 44px;
  display: block;
}
.pmc-opt-add,
.pmc-opt-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  box-sizing: border-box;
}
.pmc-opt-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #b7dfc6;
  background: #f5faf7;
  color: #059669;
  cursor: pointer;
}
.pmc-opt-thumb {
  display: block;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
}
.pmc-opt-photo-clear {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #e11d48;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  cursor: pointer;
}

.pmc-hot-picker {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 920px;
  margin-bottom: 18px;
}
.pmc-hot-refresh {
  flex-shrink: 0;
  margin-top: 2px;
}
.pmc-hot-opt-name {
  float: left;
  color: #0f172a;
}
.pmc-hot-opt-meta {
  float: right;
  color: #94a3b8;
  font-size: 12px;
  margin-left: 12px;
}
.pmc-hot-selected {
  max-width: 920px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pmc-hot-selected-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.pmc-hot-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}
.pmc-hot-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pmc-hot-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
</style>
